import ServiceOverlay from "@churchofjesuschrist/maps-google-service-overlay";
import PolygonsLayer from "@churchofjesuschrist/maps-google-polygons-layer";
import {getOverlayBounds, polygonsMask} from "@churchofjesuschrist/maps-google-overlays";
import MaskOverlay from "@churchofjesuschrist/maps-google-mask";

export default function CustomBoundaryController($q, $scope, $state, $timeout, google, DataConst, DataService, MapDrawService, MapService, NotificationService, TitleService, utils, MosFrameAnalyticsService) {
	"ngInject";
	const CUSTOM_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME = 'Maps - Custom Area Form';
	const vm = this;

	vm.$onInit = () => {
		vm.customId = $state.params.customId;
		vm.create = !vm.customId;
		vm.loading = true;
		vm.history = [];

		DataService.setMapOpts({labelsClean: true});

		DataService.getCustom(vm.customId).then((custom) => {
			TitleService.setTitleTranslate(vm.create ? "pros-maps.custom.boundary.create" : "pros-maps.custom.boundary.title", custom);

			vm.custom = angular.extend({type: DataConst.CUSTOM_TYPE}, custom);
			vm.map = DataService.instantMap();
			vm.mode = "select";
			vm.selectable = [];
			vm.affectedAreas = [];
			return MapDrawService.getCustomSelectablePolygons(custom).then((results) => {
				vm.overlays = [];
				vm.fixedBoundaries = results.fixedBoundaries;
				vm.border = results.border;
				vm.bounds = getOverlayBounds(vm.border);
				vm.border.setOptions(angular.extend({map: vm.map}, MapDrawService.styles.POLYGON_HIGHLIGHT));
				vm.overlays.push(vm.border);
				vm.recenter();

				vm.prosAreas = results.involvedOrgs.filter((org) => (org.type === "AREA" && org.boundary));

				vm.mask = polygonsMask(vm.border, {map: vm.map});
				vm.overlays.push(vm.mask);

				//the boundaries of the involvedOrgs
				vm.overlays.push(new ServiceOverlay({
					ids: results.involvedOrgs.map(MapService.getIdentity),
					map: vm.map
				}));

				results.selectable.forEach(vm.initSelectablePolygon);
				vm.updateAffectedAreas();

				//the clickable polygons on the map
				vm.layer = new PolygonsLayer({border: vm.border, polygons: vm.selectable});
				vm.layer.addListener("ready", () => {
					$timeout(() => {
						vm.loading = false;
						if ($scope.$$destroyed) {
							vm.cleanup();
						}
					});
				});
				MosFrameAnalyticsService.formStart(CUSTOM_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME);
			});
		}, NotificationService.exception(vm));

		$scope.$watch(() => (vm.mode), vm.updateMode);

		$scope.$on("$destroy", vm.cleanup);
	};

	vm.cleanup = () => {
		MapDrawService.removeOverlays(vm.overlays);
		vm.removeDrawing();
		if (vm.listeners) {
			vm.listeners.forEach((listener) => {
				google.maps.event.removeListener(listener);
			});
		}
	};

	vm.removeDrawing = () => {
		MapDrawService.removeDrawing(vm.drawing);
		delete vm.drawing;
		vm.setMasked(false);
	};

	vm.recenter = () => {
		MapService.centerMap(vm.custom, vm.map);
	};

	vm.updateMode = () => {
		if (vm.mode && vm.map) {
			let drawMode = vm.mode === "draw";

			vm.removeDrawing();

			vm.setMasked(drawMode);
			if (drawMode) {
				vm.drawing = MapDrawService.createDrawing(vm.map);
			}
		}
	};

	vm.setMasked = (enable) => {
		if (enable && !vm.masked) {
			vm.masked = new MaskOverlay({ map: vm.map })
			vm.listeners = [
				vm.masked.addListener("click", vm.onMaskClick),
				vm.masked.addListener("mousemove", vm.onMaskMouseMove)
			];
		} else {
			vm.masked?.setMap(enable ? vm.map : null);
		}
	};

	vm.initSelectablePolygon = (polygon) => {
		polygon.addListener("click", () => {
			$timeout(() => {
				vm.polygonClicked(polygon);
			});
		});
		vm.selectable.push(polygon);
		vm.overlays.push(polygon);
		polygon.setMap(vm.map);
	};

	vm.addSelectablePolygon = (polygon) => (
		MapDrawService.createSelectablePolygon(polygon).then((polygon) => {
			vm.initSelectablePolygon(polygon);
			return polygon;
		})
	);

	vm.selectPolygon = (polygon, select) => {
		MapDrawService.selectCustomPolygon(polygon, select);
		vm.updateAffectedAreas();
	};

	vm.getAffectedAreas = () => {
		let entries = [],
			selectedPolygons = vm.selectable.filter(MapDrawService.isPolygonSelected);
		selectedPolygons.forEach((polygon) => {
			let metadata = polygon.get("data");
			if (!metadata.affectedAreas) {
				metadata.affectedAreas = [];
				vm.prosAreas.map((area) => {
					entries.push({
						shape: polygon,
						polygon: area.boundary,
						result: {metadata, area}
					});
				});
			}
		});
		return MapDrawService.containsPartialShape(entries).then((results) => {
			results.forEach((result) => {
				result.metadata.affectedAreas.push(result.area);
			});

			let affectedAreas = [];
			selectedPolygons.forEach((polygon) => {
				let metadata = polygon.get("data");
				affectedAreas.push(...metadata.affectedAreas);
			});

			return utils.uniqueOrgs(affectedAreas)
				.sort(utils.sortOrgs);
		});
	};

	vm.updateAffectedAreas = () => {
		vm.loadingAffectedAreas = true;
		let currentFetch = vm.fetchingAreas = vm.getAffectedAreas();

		currentFetch.then((areas) => {
			if (currentFetch === vm.fetchingAreas) {
				vm.affectedAreas = areas;
				vm.loadingAffectedAreas = false;
			}
		});
	};

	vm.togglePolygon = (polygon) => {
		let select = !MapDrawService.isPolygonSelected(polygon);
		vm.selectPolygon(polygon, select);
	};

	vm.polygonClicked = (polygon) => {
		switch (vm.mode) {
			case "select":
				vm.togglePolygon(polygon);
				vm.addHistoryEntry(MapDrawService.historyEvent.TOGGLE, polygon);
				break;
		}
	};

	vm.onMaskClick = (e) => {
		if (vm.drawing && !vm.drawing.adding && !vm.drawing.complete && !vm.loading) {
			let position = e.latLng,
				snap = MapDrawService.getSnapPosition(position, vm.drawing, vm.layer, vm.map),
				snapped = snap !== null;
			position = snap || position;
			MapDrawService.addLineSegment(position, snapped, vm.drawing, vm.layer, vm.map).then((lineSegment) => {
				if (!lineSegment.error) {
					if (vm.drawing.complete) {
						let state = MapDrawService.getCurrentState(vm.selectable, vm.drawing);
						vm.addNewSelectablePolygon().then(() => {
							vm.addHistoryEntry(MapDrawService.historyEvent.POLYGON, state);
						});
					} else {
						vm.addHistoryEntry(MapDrawService.historyEvent.VERTEX, lineSegment);
					}
				} else {
					let message = "pros-maps.error.";
					switch (lineSegment.error) {
						case "outside_border":
							message += "outside";
							break;
						case "crosses_itself":
							message += "cross";
							break;
					}
					NotificationService.warning(message);
				}
			});
		}
	};

	vm.onMaskMouseMove = (e) => {
		let position = e.latLng;
		MapDrawService.refreshMouseTrail(position, vm.drawing, vm.layer, vm.map);
	};

	vm.addNewSelectablePolygon = () => {
		vm.loading = true;

		let polygon = new google.maps.Polygon({path: vm.drawing.line.getPath().getArray().slice(1)});
		return MapDrawService.adjustPolygonsLayer(vm.selectable, polygon, vm.fixedBoundaries).then((results) => {
			vm.removeDrawing();

			MapDrawService.removeOverlays(results.replaced);
			results.replaced.forEach((polygon) => {
				utils.removeItem(polygon, vm.selectable);
			});

			let selectCreated = false;
			results.adjustments.forEach((polygon) => {
				let previousMetadata = polygon.get("data");
				vm.addSelectablePolygon(polygon);
				if (previousMetadata) {
					if (previousMetadata.selected) {
						vm.togglePolygon(polygon);
					} else if (previousMetadata.selected === false) {
						selectCreated = true;
					}
				}
			});
			if (selectCreated) {
				results.created.forEach(vm.togglePolygon);
			}

			vm.layer.setExclusions(null);
			vm.layer.setPolygons(vm.selectable);
			vm.updateMode();

			vm.loading = false;

			return results;
		}, NotificationService.exception(vm));
	};

	vm.addHistoryEntry = (type, data) => {
		let entry = MapDrawService.createHistoryEntry(type, data, vm.selectable);
		vm.history.push(entry);
	};

	vm.undo = () => {
		let entry = vm.history.pop();
		switch (entry.type) {
			case MapDrawService.historyEvent.TOGGLE:
				let polygon = vm.selectable[entry.index];
				vm.togglePolygon(polygon);
				break;
			case MapDrawService.historyEvent.VERTEX:
				MapDrawService.removeLastLineSegment(vm.drawing, vm.layer, vm.map);
				break;
			case MapDrawService.historyEvent.POLYGON:
				let state = entry.state;

				MapDrawService.removeOverlays(vm.selectable);
				vm.selectable = state.selectable.map((item) => {
					let polygon = item.polygon;
					vm.selectPolygon(polygon, item.selected);
					polygon.setMap(vm.map);
					return polygon;
				});
				vm.layer.setExclusions(null);
				vm.layer.setPolygons(vm.selectable);

				vm.drawing = MapDrawService.restoreDrawing(state.drawing.path, state.drawing.vertices, vm.map);
				MapDrawService.removeLastLineSegment(vm.drawing, vm.layer, vm.map);

				vm.updateAffectedAreas();
				break;
		}
	};

	vm.done = () => {
		vm.mode = "validate";
	};

	vm.back = () => {
		vm.mode = "select";
	};

	vm.save = () => {
		vm.loading = true;
		//TODO: handle case where they don't select or draw any polygons

		DataService.getMission().then((mission) => (
			MapService.merge(vm.selectable.filter(MapDrawService.isPolygonSelected)).then((merged) => {
				vm.custom.boundary = merged;
				let fn = vm.create ? MapService.createCustom : MapService.updateCustom;
				return fn(vm.custom, mission).then((custom) => (
					DataService.updateCustom(custom).then((custom) => {
						NotificationService.success("pros-maps.success.saved.custom", null, custom);
						MosFrameAnalyticsService.formSubmit(CUSTOM_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME);
						vm.exit();
					})
				));
			})
		), () => {
			NotificationService.exception(vm);
			MosFrameAnalyticsService.formError(CUSTOM_BOUNDARY_ADOBE_ANALYTICS_FORM_NAME, ['server-error']);
		});
	};

	vm.exit = (returnToMission) => {
		vm.create || returnToMission ? $state.go("mission") : $state.go("custom", {customId: vm.customId});
	};

}
